import { defineStore } from 'pinia';
import { getAntragId } from '@/api/AntragService.js';
import { useMainStore } from './useMainStore.js';

export const useAntragStore = defineStore('antrag', {
    state: () => ({
        // Antragsdaten
        antragId: '',
        kanal: '',
        
        // Fehlerbehandlung
        returnCode: 0,
        nachricht: '',
        
        // Status
        isLoading: false,
        lastRequestTime: null,

        // Verwendung
        statusVerwendung: 0,
    }),
    
    getters: {
        /**
         * Prüft ob eine gültige AntragId vorhanden ist
         * @returns {boolean}
         */
        hasValidAntragId: (state) => {
            return state.antragId !== '' && state.returnCode === 0;
        },
        
        /**
         * Prüft ob ein Fehler vorliegt
         * @returns {boolean}
         */
        hasError: (state) => {
            return state.returnCode !== 0;
        },
    },
    
    actions: {
        /**
         * Generiert eine neue AntragId über die API
         * Verwendet den Kanal aus dem MainStore und prüft auf Konsistenz mit bestehendem Antrag
         * @returns {Promise<boolean>} true bei Erfolg, false bei Fehler
         */
        async generateAntragId() {
            // Kanal aus MainStore holen
            const mainStore = useMainStore();
            const aktuellerKanal = mainStore.kanal;
            /*
            console.log('[AntragStore] generateAntragId aufgerufen');
            console.log('[AntragStore] Aktueller Kanal aus MainStore:', aktuellerKanal);
            console.log('[AntragStore] Gespeicherte AntragId:', this.antragId || '(leer)');
            console.log('[AntragStore] Gespeicherter Kanal:', this.kanal || '(leer)');
            */
            // Konsistenzprüfung: Ist bereits eine AntragId vorhanden?
            if (this.antragId !== '') {
                //console.log('[AntragStore] AntragId vorhanden, prüfe Kanal-Konsistenz...');
                
                // Prüfung: Stimmt der gespeicherte Kanal mit dem aktuellen Kanal überein?
                if (this.kanal === aktuellerKanal) {
                    // Kanal stimmt überein → Bestehende AntragId verwenden, KEIN API Call
                    //console.log('[AntragStore] Kanal-Konsistenz OK → Bestehende AntragId wird wiederverwendet:', this.antragId);
                    return true;
                } else {
                    // Kanal hat sich geändert → Neue AntragId muss generiert werden
                    //console.log('[AntragStore] Kanal-Inkonsistenz erkannt!');
                    //console.log('[AntragStore] Gespeicherter Kanal:', this.kanal, '→ Neuer Kanal:', aktuellerKanal);
                    //console.log('[AntragStore] Neue AntragId wird generiert...');
                    // Weiter mit API Call unten
                }
            } else {
                //console.log('[AntragStore] Keine AntragId vorhanden → Neue AntragId wird generiert');
            }
            
            // Ab hier: API Call durchführen
            // Loading-Status setzen
            this.isLoading = true;
            this.lastRequestTime = new Date().toISOString();
            
            // Kanal speichern (wird bei Erfolg/Fehler entsprechend aktualisiert)
            this.kanal = aktuellerKanal;
            
            try {
                // API Call
                //console.log('[AntragStore] API Call wird durchgeführt mit Kanal:', aktuellerKanal);
                const result = await getAntragId(aktuellerKanal);
                
                if (result.success) {
                    // Erfolg: AntragId speichern, Fehler zurücksetzen
                    this.antragId = result.antragId;
                    this.returnCode = 0;
                    this.nachricht = '';
                    this.statusVerwendung = 0;
                    //console.log('[AntragStore] AntragId erfolgreich generiert:', this.antragId);
                    this.isLoading = false;
                    return true;
                    
                } else {
                    // Fehler: AntragId leeren, Fehlerinfo speichern
                    this.antragId = '';
                    
                    // Bei Netzwerkfehler (HTTP 4xx/5xx) oder wenn kein Status vorhanden
                    if (!result.status || result.status >= 400) {
                        this.returnCode = 99;
                        this.nachricht = 'Netzwerkfehler';
                        console.error('[AntragStore] Netzwerkfehler bei API-Aufruf');
                    } else {
                        // API-Fehler mit ReturnCode aus Response
                        // Da die API im Fehlerfall möglicherweise keinen returnCode sendet, 
                        // verwenden wir 99 als Fallback
                        this.returnCode = 99;
                        this.nachricht = result.error || 'Unbekannter Fehler';
                        console.error('[AntragStore] API-Fehler:', this.nachricht);
                    }
                    
                    this.isLoading = false;
                    return false;
                }
                
            } catch (error) {
                // Exception: AntragId leeren, Netzwerkfehler setzen
                console.error('[AntragStore] Exception beim API-Aufruf:', error);
                this.antragId = '';
                this.returnCode = 99;
                this.nachricht = 'Netzwerkfehler';
                this.isLoading = false;
                return false;
            }
        },
        
        /**
         * Setzt den Store zurück
         */
        resetAntrag() {
            //console.log('[AntragStore] Store wird zurückgesetzt');
            this.antragId = '';
            this.kanal = '';
            this.returnCode = 0;
            this.nachricht = '';
            this.isLoading = false;
            this.lastRequestTime = null;
            this.statusVerwendung = 0;
        },
        
        /**
         * Löscht nur die Fehlerinformationen
         */
        clearError() {
            this.returnCode = 0;
            this.nachricht = '';
        },
    },
    
    persist: {
        storage: sessionStorage
    }
});
